import { useCallback, useMemo } from 'react';

import { type GlobalShortcutsItem } from '@atlassian/global-shortcuts';

import { useItemsAndShortcuts } from '../../../controllers/items-and-shortcuts-provider';
import { useOptions } from '../../../controllers/options-provider';
import { useUserMenuDataShapes } from '../../../controllers/user-state/user-menu-data-shapes-provider';
import { INTERNAL_GLOBAL_ITEMS } from '../../../ui/more-menu/constants';
import { type SideNavViewedSideNavAppShortcutsSectionAttributesType } from '../../analytics/generated/analytics.types';
import { useAnalyticsEvents } from '../../analytics/generated/use-analytics-events';

export function useNavAnalytics() {
	const { fireEvent } = useAnalyticsEvents();

	const {
		hiddenMenuIds,
		hiddenItemMenuIds,
		hiddenShortcutMenuIds,
		visibleMenuIds,
		visibleItemMenuIds,
		visibleShortcutMenuIds,
	} = useUserMenuDataShapes();
	const { disableMenuCustomization, hasBottomElement } = useOptions();
	const { allItems, globalItems, growthItemShortcuts, items, shortcuts } = useItemsAndShortcuts();

	// Global items + customize/more + bottom element
	const globalItemsList = useMemo(
		() => [
			...visibleItemMenuIds.filter((id) => globalItems.has(id)),
			...(!disableMenuCustomization
				? [
						!!hiddenItemMenuIds.length || !!hiddenShortcutMenuIds.length
							? INTERNAL_GLOBAL_ITEMS.more
							: INTERNAL_GLOBAL_ITEMS.customize,
					]
				: []),
			...(hasBottomElement ? [INTERNAL_GLOBAL_ITEMS.bottomElem] : []),
		],
		[
			disableMenuCustomization,
			hasBottomElement,
			hiddenItemMenuIds,
			hiddenShortcutMenuIds,
			globalItems,
			visibleItemMenuIds,
		],
	);

	// App specific items
	const appSpecificItemsList = visibleItemMenuIds.filter(
		(id) => allItems[id] && !globalItems.has(id),
	);

	// Shortcuts + growth shortcuts
	const shortcutItemList = useMemo(
		() => [...visibleShortcutMenuIds, ...Object.keys(growthItemShortcuts ?? {})],
		[visibleShortcutMenuIds, growthItemShortcuts],
	);

	// All visible items
	const navItemList = useMemo(
		() => [...globalItemsList, ...appSpecificItemsList, ...shortcutItemList],
		[globalItemsList, appSpecificItemsList, shortcutItemList],
	);

	const getItemGroup = useCallback(
		(id: string) => {
			return !!growthItemShortcuts?.[id] || !!shortcuts?.[id]
				? 'appShortcut'
				: Object.keys(items).includes(id)
					? 'appSpecific'
					: 'global';
		},
		[growthItemShortcuts, items, shortcuts],
	);

	const getGroupItems = useCallback(
		(group: string) => {
			switch (group) {
				case 'global':
					return globalItemsList;
				case 'appSpecific':
					return appSpecificItemsList;
				case 'appShortcut':
					return shortcutItemList;
				default:
					return [];
			}
		},
		[appSpecificItemsList, globalItemsList, shortcutItemList],
	);

	const fireViewedSideNavEvent = useCallback(() => {
		const moreMenuItems = hiddenItemMenuIds.concat(hiddenShortcutMenuIds);

		const globalItemsList = [
			...globalItems.keys(),
			...(!disableMenuCustomization
				? [moreMenuItems.length ? INTERNAL_GLOBAL_ITEMS.more : INTERNAL_GLOBAL_ITEMS.customize]
				: []),
			...(hasBottomElement ? [INTERNAL_GLOBAL_ITEMS.bottomElem] : []),
		];

		const appSpecificItemsList = Object.keys(items);
		const appShortcutItemsList = visibleShortcutMenuIds;

		fireEvent('ui.sideNav.viewed.sideNavMenu', {
			globalItemsCount: globalItemsList.length,
			globalItemsList,
			appSpecificItemsCount: appSpecificItemsList.length,
			appSpecificItemsList,
			appShortcutItemsCount: appShortcutItemsList.length,
			appShortcutItemsList,
			inMoreItemsCount: moreMenuItems.length,
			inMoreItemsList: moreMenuItems,
		});
	}, [
		hiddenItemMenuIds,
		hiddenShortcutMenuIds,
		globalItems,
		disableMenuCustomization,
		hasBottomElement,
		items,
		visibleShortcutMenuIds,
		fireEvent,
	]);

	const fireClickedSideNavItemEvent = useCallback(
		(id: string, navLayoutComponent?: 'main' | 'preMain' | 'postMain' | 'bottom') => {
			// Assign the current group based on the item type
			const currentGroup = getItemGroup(id);
			const isInternalGlobalItem = Object.values(INTERNAL_GLOBAL_ITEMS).includes(id);
			// Get visible items based on their group
			const visibleGroupItems = getGroupItems(currentGroup);

			const isInMore =
				// customize is in more if there are any hidden items
				(INTERNAL_GLOBAL_ITEMS.customize === id && !!hiddenMenuIds.length) ||
				hiddenMenuIds.includes(id);

			// Current index irrespective of the item group
			const currentIndex = isInMore
				? -1
				: // More and cusomise and growth shortcuts are always at the end of the list
					isInternalGlobalItem || !!growthItemShortcuts?.[id]
					? visibleMenuIds.length
					: visibleMenuIds.indexOf(id);

			// Current index within the item group
			const currentGroupIndex = isInMore ? -1 : visibleGroupItems.indexOf(id);

			const currentComponent = navLayoutComponent
				? navLayoutComponent
				: isInMore
					? 'postMain'
					: 'main';

			fireEvent('ui.sideNav.clicked.sideNavItem', {
				navLayoutComponent: currentComponent,
				navItemsCount: navItemList.length,
				navItemsIndex: currentIndex,
				group: currentGroup,
				groupItemsCount: visibleGroupItems.length,
				groupItemsIndex: currentGroupIndex,
				groupItem: id,
				isInMore,
			});
		},
		[
			fireEvent,
			getGroupItems,
			getItemGroup,
			growthItemShortcuts,
			hiddenMenuIds,
			navItemList.length,
			visibleMenuIds,
		],
	);

	const fireViewedSideNavSubMenuEvent = useCallback(
		(id: string, subItemsCount?: number, isInMore: boolean = false) => {
			const isGlobal = globalItems.has(id);

			const visibleGlobalItems = visibleItemMenuIds.filter((id) => globalItems.has(id));
			const visibleAppSpecificItems = visibleItemMenuIds.filter((id) => allItems[id]);
			const currentGroup = isGlobal ? visibleGlobalItems : visibleAppSpecificItems;
			fireEvent('ui.sideNav.viewed.sideNavSubMenu', {
				navLayoutComponent: 'main',
				group: isGlobal ? 'global' : 'appSpecific',
				groupItemsCount: currentGroup.length,
				groupItemsIndex: currentGroup.findIndex((key) => key === id),
				groupItem: id,
				isInMore,
				groupSubItemCount: subItemsCount ?? null,
			});
		},
		[allItems, fireEvent, globalItems, visibleItemMenuIds],
	);

	const fireViewedShortcutsEvent = useCallback(
		(shortcuts: GlobalShortcutsItem[]) => {
			const attributes: SideNavViewedSideNavAppShortcutsSectionAttributesType = {
				appShortcutItemsCount: shortcuts.length,
				appShortcutItemsList: shortcuts.map((item) => item.label),
			};
			fireEvent('ui.sideNav.viewed.sideNavAppShortcutsSection', attributes);
		},
		[fireEvent],
	);

	const fireClickedSideNavSubMenuItemEvent = useCallback(
		(id: string, subItemsCount?: number, isInMore: boolean = false) => {
			const isGlobal = globalItems.has(id);
			const visibleGlobalItems = visibleItemMenuIds.filter((id) => globalItems.has(id));
			const visibleAppSpecificItems = visibleItemMenuIds.filter((id) => allItems[id]);
			const currentGroup = isGlobal ? visibleGlobalItems : visibleAppSpecificItems;

			fireEvent('ui.sideNav.clicked.sideNavSubItem', {
				navLayoutComponent: isInMore ? 'postMain' : 'main',
				group: isGlobal ? 'global' : 'appSpecific',
				groupItemsCount: currentGroup.length,
				groupItemsIndex: currentGroup.findIndex((key) => key === id),
				groupItem: id,
				isInMore,
				groupSubItemCount: subItemsCount ?? null,
				groupSubItemIndex: null,
				groupSubItem: null,
			});
		},
		[allItems, fireEvent, globalItems, visibleItemMenuIds],
	);

	return {
		fireViewedSideNavEvent,
		fireClickedSideNavItemEvent,
		fireViewedSideNavSubMenuEvent,
		fireViewedShortcutsEvent,
		fireClickedSideNavSubMenuItemEvent,
	};
}
